/**
 * Hook pour gérer les templates d'emails personnalisés avec Realtime
 */

import { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";
import { EmailTemplate } from "@/types/emailTemplates";

export function useEmailTemplates() {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Fetch initial des templates de l'utilisateur
    const fetchTemplates = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase
          .from("email_templates")
          .select("*")
          .order("created_at", { ascending: false });

        if (fetchError) throw fetchError;

        setTemplates((data as EmailTemplate[]) || []);
      } catch (err: any) {
        console.error("❌ Erreur lors du chargement des templates:", err);
        setError(err.message || "Erreur inconnue");
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();

    // Subscription Realtime pour les changements
    const channel = supabase
      .channel("email_templates_changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "email_templates",
        },
        (payload) => {
          console.log("🔄 [Realtime] Changement dans email_templates:", payload);

          if (payload.eventType === "INSERT") {
            setTemplates((prev) => [payload.new as EmailTemplate, ...prev]);
          } else if (payload.eventType === "UPDATE") {
            setTemplates((prev) =>
              prev.map((t) =>
                t.id === payload.new.id ? (payload.new as EmailTemplate) : t
              )
            );
          } else if (payload.eventType === "DELETE") {
            setTemplates((prev) => prev.filter((t) => t.id !== payload.old.id));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  /**
   * Créer un nouveau template
   */
  const createTemplate = async (
    name: string,
    body: string,
    color: string = "#3B82F6"
  ): Promise<{ success: boolean; error?: string; template?: EmailTemplate }> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { success: false, error: "Utilisateur non authentifié" };
      }

      const { data, error: insertError } = await supabase
        .from("email_templates")
        .insert({
          user_id: user.id,
          name,
          body,
          color,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      console.log("✅ Template créé:", data);
      return { success: true, template: data as EmailTemplate };
    } catch (err: any) {
      console.error("❌ Erreur lors de la création du template:", err);
      return { success: false, error: err.message || "Erreur inconnue" };
    }
  };

  /**
   * Mettre à jour un template existant
   */
  const updateTemplate = async (
    id: string,
    updates: Partial<Pick<EmailTemplate, "name" | "body" | "color">>
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const { error: updateError } = await supabase
        .from("email_templates")
        .update(updates)
        .eq("id", id);

      if (updateError) throw updateError;

      console.log("✅ Template mis à jour:", id);
      return { success: true };
    } catch (err: any) {
      console.error("❌ Erreur lors de la mise à jour du template:", err);
      return { success: false, error: err.message || "Erreur inconnue" };
    }
  };

  /**
   * Supprimer un template
   */
  const deleteTemplate = async (
    id: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const { error: deleteError } = await supabase
        .from("email_templates")
        .delete()
        .eq("id", id);

      if (deleteError) throw deleteError;

      console.log("✅ Template supprimé:", id);
      return { success: true };
    } catch (err: any) {
      console.error("❌ Erreur lors de la suppression du template:", err);
      return { success: false, error: err.message || "Erreur inconnue" };
    }
  };

  return {
    templates,
    loading,
    error,
    createTemplate,
    updateTemplate,
    deleteTemplate,
  };
}

